import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { authStore, hasRole, type Role, type AuthUser } from "@/lib/auth-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Shield,
  MoreVertical,
  Trash2,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/users")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "admin")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "Users — Tilly POS" },
      { name: "description", content: "Manage system users and roles." },
    ],
  }),
  component: UsersPage,
});

const ALL_ROLES: Role[] = ["admin", "manager", "cashier", "waiter", "viewer"];

function UsersPage() {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const currentUser = authStore.getState().user;

  useEffect(() => {
    authStore.getUsers().then(setUsers);
    const handler = () => authStore.getUsers().then(setUsers);
    window.addEventListener("users:updated", handler);
    return () => window.removeEventListener("users:updated", handler);
  }, [refreshKey]);

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle={`${users.length} account${users.length !== 1 ? "s" : ""}`}
        right={<AddUserDialog />}
      />

      <ul className="divide-y divide-border px-4">
        {users.map((u) => {
          const isMe = currentUser?.id === u.id;
          return (
            <li key={u.id} className="flex items-center gap-3 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground font-display text-sm font-bold">
                {u.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">
                    {u.displayName}
                    {isMe && (
                      <span className="ml-1.5 text-[10px] text-primary font-normal">
                        (you)
                      </span>
                    )}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  @{u.username} · {u.id}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase ${
                    u.role === "admin"
                      ? "bg-destructive/10 text-destructive"
                      : u.role === "manager"
                        ? "bg-primary/10 text-primary"
                        : u.role === "cashier"
                          ? "bg-warning/10 text-warning-foreground"
                          : "bg-accent text-accent-foreground"
                  }`}
                >
                  {u.role}
                </span>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="rounded-md p-1 text-muted-foreground hover:text-foreground cursor-pointer">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={async () => {
                        const nextRole = prompt(
                          `Change role for ${u.displayName} (${ALL_ROLES.join(", ")}):`,
                          u.role,
                        );
                        if (nextRole && ALL_ROLES.includes(nextRole as Role)) {
                          await authStore.updateUserRole(
                            u.id,
                            nextRole as Role,
                          );
                          toast.success(`${u.displayName} is now ${nextRole}`);
                          authStore.getUsers().then(setUsers);
                        }
                      }}
                    >
                      <Shield className="h-4 w-4" />
                      Change Role
                    </DropdownMenuItem>
                    {!isMe && (
                      <>
                        <DropdownMenuItem
                          className="cursor-pointer text-destructive focus:text-destructive"
                          onClick={async () => {
                            if (
                              confirm(
                                `Delete user "${u.displayName}"? This cannot be undone.`,
                              )
                            ) {
                              await authStore.deleteUser(u.id);
                              toast.success(`${u.displayName} deleted`);
                              authStore.getUsers().then(setUsers);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AddUserDialog() {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("cashier");

  const handleSubmit = async () => {
    if (!username || !password || !displayName) {
      return toast.error("Fill all fields");
    }
    await authStore.addUser({ username, password, displayName, role });
    toast.success(`User "${displayName}" created`);
    setOpen(false);
    setUsername("");
    setPassword("");
    setDisplayName("");
    setRole("cashier");
    window.dispatchEvent(new CustomEvent("users:updated"));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary">
          <Plus className="h-4 w-4" /> New User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Display Name</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Alice"
            />
          </div>
          <div>
            <Label>Username</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. alice"
              autoComplete="off"
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label>Role</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {ALL_ROLES.map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium capitalize transition ${
                    role === r
                      ? "border-primary bg-accent text-accent-foreground"
                      : "border-border bg-card text-muted-foreground"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit}>Create User</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
